
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile } from '@/types/supabase';

// Fetch user profile data
export const fetchUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;

    // Ensure second_me_config is properly typed as Record<string, any> | null
    const profile: UserProfile = {
      ...data,
      second_me_config: data.second_me_config ? data.second_me_config as Record<string, any> : null
    };

    return profile;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
};

// Fetch user roles
export const fetchUserRoles = async (userId: string): Promise<string[]> => {
  try {
    console.log("Fetching roles for user:", userId);
    
    const { data: rolesData, error: rolesError } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);

    if (rolesError) {
      console.error("Error in SQL query:", rolesError);
      throw rolesError;
    }

    console.log("User roles data:", rolesData);
    
    // Make sure we return an empty array if no roles are found
    if (!rolesData || rolesData.length === 0) {
      return [];
    }
    
    return rolesData.map(r => r.role);
  } catch (error) {
    console.error('Error fetching user roles:', error);
    return [];
  }
};

// Sign in with email (magic link)
export const signInWithEmail = async (email: string): Promise<void> => {
  try {
    const { error } = await supabase.auth.signInWithOtp({ email });
    if (error) throw error;
    alert('Check your email for the magic link to sign in.');
  } catch (error: any) {
    alert(error.error_description || error.message);
  }
};

// Sign in with email and password
export const signInWithPassword = async (email: string, password: string): Promise<void> => {
  try {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  } catch (error: any) {
    throw error;
  }
};

// Sign up new user
export const signUpUser = async (email: string, password: string): Promise<void> => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
      },
    });
    if (error) throw error;

    // Create user profile
    if (data.user?.id) {
      await supabase
        .from('user_profiles')
        .insert({ id: data.user.id, first_name: '', last_name: '' });
    }

    alert('Check your email to verify your account.');
  } catch (error: any) {
    alert(error.error_description || error.message);
  }
};

// Sign out user
export const signOutUser = async (): Promise<void> => {
  await supabase.auth.signOut();
};
